import {useSuspenseQuery} from '@tanstack/react-query'
import {HiMiniArrowRight} from 'react-icons/hi2'
import {Link} from '@tanstack/react-router'
import {BlazeConfig} from 'blaze-slider'
import {memo, Suspense} from 'react'
import clsx from 'clsx'
import {collectionDetailsQueryOptions} from '~/client/fn/collection'
import ImageComposition from '~/components/ImageComposition'
import useLocaleParams from '~/hooks/useLocaleParams'
import Carousel from '~/components/common/Carousel'
import Card from '~/components/common/Card'

type CollectionRefProps = {
    collection: {
        id: string | null
        name: string | null
    }
    overwrites: {
        title?: string
        description?: string
    } | null
}

type CollectionLinkProps = {
    className?: string
    title?: string
    reference: CollectionRefProps
    actionLabel: string
}

const config: BlazeConfig = {
    all: {
        slidesToShow: 1.25,
        loop: false,
    },
    '(min-width: 520px)': {
        slidesToShow: 2.25,
    },
    '(min-width: 768px)': {
        slidesToShow: 3.25,
        slidesToScroll: 3,
    },
}

const CollectionLink = ({className, title, reference, actionLabel}: CollectionLinkProps) => {
    const params = useLocaleParams()
    const collectionName = title ?? reference.overwrites?.title ?? reference.collection.name

    return (
        <div className={clsx('mx-auto max-w-screen-xl px-4 sm:px-6', className)}>
            <div className="flex flex-row justify-between gap-2 items-center flex-wrap mb-4">
                <h2 className="text-2xl md:text-4xl font-bold">{collectionName}</h2>
                <Link
                    to="/$lang/collections/$collectionId"
                    params={{...params, collectionId: reference.collection.id!}}
                    className="flex flex-row items-center gap-1 font-medium text-green-600 md:text-lg"
                >
                    <span>{actionLabel}</span>
                    <HiMiniArrowRight className="size-4 md:size-5" />
                </Link>
            </div>
            <Suspense fallback={<CollectionCarouselSkeletonLoader />}>
                <CollectionCarousel collectionId={reference.collection.id!} />
            </Suspense>
        </div>
    )
}

type CollectionCarouselProps = {
    collectionId: string
}

const CollectionCarousel = ({collectionId}: CollectionCarouselProps) => {
    const params = useLocaleParams()
    const {data} = useSuspenseQuery(collectionDetailsQueryOptions(collectionId, params.locale))

    if (!data) {
        return null
    }

    return (
        <div className="relative">
            <Carousel
                config={config}
                showDots={false}
                showDisabledArrows={true}
                initialSlidesToShow={3.25}
            >
                {data.designs.map((design) => (
                    <Link
                        key={design.id}
                        className="block aspect-square h-full"
                        to="/$lang/products"
                        params={params}
                        search={{design: design?.id}}
                    >
                        <Card className="p-2 md:p-4 flex flex-col gap-2 justify-between items-center h-full">
                            <div className="h-full w-auto px-4">
                                <ImageComposition
                                    image={design?.image!}
                                    sizes="400px"
                                    className="select-none"
                                />
                            </div>
                            <div className="text-center font-bold text-sm">{design?.name}</div>
                        </Card>
                    </Link>
                ))}
            </Carousel>
        </div>
    )
}

const CollectionCarouselSkeletonLoader = memo(() => {
    return (
        <div>
            <Carousel
                config={{...config, all: {...config.all, draggable: false}}}
                showDots={false}
                showArrows={false}
            >
                {[...new Array(8)].map((_, index) => (
                    <Card key={index} className="p-4" aria-label="Loading content...">
                        <div className="p-4 aspect-square bg-gray-900/30 animate-pulse rounded w-full" />
                        <div className="m-4 bg-gray-900/30 animate-pulse rounded h-5 w-1/2" />
                    </Card>
                ))}
            </Carousel>
        </div>
    )
})

export default CollectionLink
